(() => {
  const initialize = root => {
    if (root.dataset.crispiVariantsReady) return;
    const gallery = root.querySelector('[data-crispi-gallery]');
    const form = root.querySelector('[data-product-form]');
    if (!gallery || !form) return;
    root.dataset.crispiVariantsReady = 'true';
    const slides = [...gallery.querySelectorAll('[data-crispi-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-crispi-thumb]')];
    const prev = gallery.querySelector('[data-crispi-prev]');
    const next = gallery.querySelector('[data-crispi-next]');
    const dots = gallery.querySelector('[data-crispi-dots]');
    const count = gallery.querySelector('[data-crispi-gallery-count]');
    const quantity = form.querySelector('input[name="quantity"]');
    const orderTotal = form.querySelector('[data-crispi-order-total]');
    const totalFormatter = new Intl.NumberFormat(root.dataset.locale || 'de', { style: 'currency', currency: root.dataset.currency || 'EUR' });
    const select = form.querySelector('[data-variant-select]');
    const links = [...root.querySelectorAll('[data-crispi-variant]')];
    const status = root.querySelector('[data-crispi-variant-status]');
    const buyButtons = [...root.querySelectorAll('[data-add-to-cart],[data-sticky-add]')];
    const express = form.querySelector('.payment-security-express');
    const groups = new Map();
    slides.forEach(slide => {
      const colour = slide.dataset.crispiColour;
      if (!groups.has(colour)) groups.set(colour, []);
      groups.get(colour).push(slide);
    });
    const variants = new Map();
    if (select) {
      [...select.options].forEach(option => variants.set(option.value, {
        id: option.value,
        colour: option.dataset.crispiColour || option.textContent.trim(),
        sku: option.dataset.crispiSku || '',
        price: Number(option.dataset.crispiPrice),
        imageId: option.dataset.crispiImageId || '',
        available: option.dataset.available === 'true'
      }));
    } else {
      const id = form.querySelector('input[name="id"]')?.value;
      if (id) variants.set(id, {
        id,
        colour: gallery.dataset.initialColour,
        sku: root.querySelector('[data-crispi-sku-value]')?.textContent.trim() || '',
        imageId: gallery.querySelector('[data-crispi-slide].is-active')?.dataset.imageId || '',
        available: !buyButtons[0]?.disabled
      });
    }
    let committedId = String(root.dataset.selectedVariantId || select?.value || variants.keys().next().value || '');
    let currentColour = variants.get(committedId)?.colour || gallery.dataset.initialColour;
    const initialSlides = groups.get(currentColour) || [];
    let activeIndex = Math.max(0, initialSlides.findIndex(slide => !slide.hidden));
    let requestedIndex = activeIndex;
    let requestNumber = 0;
    let variantLoading = false;
    let committingSelect = false;
    let dotsColour;
    let touchStart;
    const decoding = new WeakMap();
    const updateTotal = () => {
      const variant = variants.get(committedId);
      const units = Math.max(1, Math.floor(Number(quantity?.value) || 1));
      if (!variant || !Number.isFinite(variant.price)) return;
      const total = totalFormatter.format(variant.price * units / 100);
      if (orderTotal) {
        orderTotal.hidden = units <= 1;
        orderTotal.textContent = `${units} ${root.dataset.totalLabel}: ${total}`;
      }
      const stickyPrice = root.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = total;
    };
    const setStatus = (message = '', error = false) => {
      if (!status) return;
      status.textContent = message;
      status.classList.toggle('is-error', error);
    };
    const cartBusy = () => buyButtons.some(button => button.getAttribute('aria-busy') === 'true');
    const setVariantLoading = loading => {
      variantLoading = loading;
      root.dataset.crispiVariantLoading = String(loading);
      const available = Boolean(variants.get(committedId)?.available);
      buyButtons.forEach(button => { button.disabled = loading || cartBusy() || !available; });
      if (express) {
        express.inert = loading;
        express.hidden = !available;
        if (loading) express.setAttribute('aria-busy', 'true');
        else express.removeAttribute('aria-busy');
      }
    };
    const decodeSlide = slide => {
      const image = slide?.querySelector('img');
      if (!image) return Promise.resolve(false);
      image.loading = 'eager';
      if (image.complete && image.naturalWidth > 0) return Promise.resolve(true);
      if (decoding.has(image)) return decoding.get(image);
      let timer;
      let onLoad;
      let onError;
      const ready = new Promise(resolve => {
        if (typeof image.decode === 'function') {
          image.decode().then(() => resolve(image.naturalWidth > 0)).catch(() => resolve(image.complete && image.naturalWidth > 0));
        } else {
          onLoad = () => resolve(image.naturalWidth > 0);
          onError = () => resolve(false);
          image.addEventListener('load', onLoad, { once: true });
          image.addEventListener('error', onError, { once: true });
        }
        timer = setTimeout(() => resolve(false), 12000);
      }).finally(() => {
        clearTimeout(timer);
        if (onLoad) image.removeEventListener('load', onLoad);
        if (onError) image.removeEventListener('error', onError);
        decoding.delete(image);
      });
      decoding.set(image, ready);
      return ready;
    };
    const wrap = (index, length) => length ? (index + length) % length : 0;
    const prefetchNeighbours = () => {
      const group = groups.get(currentColour) || [];
      if (group.length < 2) return;
      decodeSlide(group[wrap(activeIndex + 1, group.length)]);
      decodeSlide(group[wrap(activeIndex - 1, group.length)]);
    };
    const renderGallery = (colour, index) => {
      const group = groups.get(colour) || [];
      if (!group.length) return false;
      const targetIndex = wrap(index, group.length);
      const target = group[targetIndex];
      target.hidden = false;
      slides.forEach(slide => {
        slide.hidden = slide !== target;
        slide.classList.toggle('is-active', slide === target);
      });
      thumbs.forEach(thumb => {
        const belongs = thumb.dataset.crispiColour === colour;
        const thumbIndex = group.findIndex(slide => slide.dataset.imageId === thumb.dataset.imageId);
        const selected = belongs && thumbIndex === targetIndex;
        thumb.hidden = !belongs;
        thumb.classList.toggle('is-active', selected);
        thumb.setAttribute('aria-pressed', String(selected));
        if (belongs) thumb.setAttribute('aria-label', `${root.dataset.imageLabel} ${thumbIndex + 1} / ${group.length}`);
      });
      if (count) count.textContent = `${targetIndex + 1} / ${group.length}`;
      currentColour = colour;
      activeIndex = targetIndex;
      requestedIndex = targetIndex;
      if (dots && dotsColour !== colour) {
        const fragment = document.createDocumentFragment();
        group.forEach((slide, dotIndex) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.setAttribute('aria-label', `${root.dataset.imageLabel} ${dotIndex + 1} / ${group.length}`);
          button.addEventListener('click', () => showSlide(dotIndex));
          fragment.append(button);
        });
        dots.replaceChildren(fragment);
        dotsColour = colour;
      }
      [...(dots?.children || [])].forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === targetIndex);
        dot.setAttribute('aria-pressed', String(dotIndex === targetIndex));
      });
      const activeDot = dots?.querySelector('.is-active');
      if (activeDot && dots.clientWidth) dots.scrollTo({ left: Math.max(0, activeDot.offsetLeft - (dots.clientWidth - activeDot.offsetWidth) / 2), behavior: 'auto' });
      if (prev) prev.disabled = group.length < 2;
      if (next) next.disabled = group.length < 2;
      prefetchNeighbours();
      return true;
    };
    const showSlide = async index => {
      if (variantLoading) return;
      const group = groups.get(currentColour) || [];
      if (!group.length) return;
      requestedIndex = wrap(index, group.length);
      const targetIndex = requestedIndex;
      const colour = currentColour;
      const ticket = ++requestNumber;
      const ready = await decodeSlide(group[targetIndex]);
      if (ticket !== requestNumber || !root.isConnected) return;
      if (!ready) {
        requestedIndex = activeIndex;
        setStatus(root.dataset.errorLabel, true);
        return;
      }
      renderGallery(colour, targetIndex);
      setStatus(`${root.dataset.imageLabel} ${targetIndex + 1} / ${group.length}`);
    };
    const syncVariant = variant => {
      committedId = variant.id;
      root.dataset.selectedVariantId = variant.id;
      if (select) {
        select.value = variant.id;
        committingSelect = true;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
        committingSelect = false;
      }
      root.querySelectorAll('[data-crispi-sku-value]').forEach(node => { node.textContent = variant.sku; });
      root.querySelectorAll('[data-crispi-colour-label]').forEach(node => { node.textContent = variant.colour; });
      links.forEach(link => {
        if (link.dataset.crispiVariant === variant.id) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      buyButtons.forEach(button => { button.textContent = variant.available ? root.dataset.addLabel : root.dataset.soldoutLabel; });
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      updateTotal();
    };
    const requestVariant = async id => {
      const variant = variants.get(String(id));
      if (!variant) return;
      if (cartBusy()) {
        if (select) select.value = committedId;
        setStatus(root.dataset.cartBusyLabel);
        return;
      }
      const ticket = ++requestNumber;
      if (variant.id === committedId) {
        if (select) select.value = committedId;
        setVariantLoading(false);
        setStatus('');
        return;
      }
      const group = groups.get(variant.colour) || [];
      if (!group.length) {
        if (select) select.value = committedId;
        setVariantLoading(false);
        setStatus(root.dataset.errorLabel, true);
        return;
      }
      const associatedIndex = group.findIndex(slide => slide.dataset.imageId === variant.imageId);
      const firstIndex = associatedIndex >= 0 ? associatedIndex : 0;
      setVariantLoading(true);
      setStatus(`${root.dataset.loadingLabel} ${variant.colour}`);
      const ready = await decodeSlide(group[firstIndex]);
      if (ticket !== requestNumber || !root.isConnected) return;
      if (!ready) {
        if (select) select.value = committedId;
        setVariantLoading(false);
        setStatus(root.dataset.errorLabel, true);
        return;
      }
      renderGallery(variant.colour, firstIndex);
      syncVariant(variant);
      setVariantLoading(false);
      setStatus(`${root.dataset.selectedLabel}: ${variant.colour}${variant.available ? '.' : ` · ${root.dataset.soldoutLabel}`}`);
    };
    links.forEach(link => link.addEventListener('click', event => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!variants.has(link.dataset.crispiVariant)) return;
      event.preventDefault();
      requestVariant(link.dataset.crispiVariant);
    }));
    select?.addEventListener('change', event => {
      if (committingSelect) return;
      const requestedId = select.value;
      select.value = committedId;
      event.stopImmediatePropagation();
      requestVariant(requestedId);
    }, true);
    form.addEventListener('submit', event => {
      if (!variantLoading) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setStatus(root.dataset.loadingLabel);
    }, true);
    thumbs.forEach(thumb => thumb.addEventListener('click', () => {
      if (variantLoading || thumb.dataset.crispiColour !== currentColour) return;
      const group = groups.get(currentColour) || [];
      const index = group.findIndex(slide => slide.dataset.imageId === thumb.dataset.imageId);
      if (index >= 0) showSlide(index);
    }));
    prev?.addEventListener('click', () => showSlide(requestedIndex - 1));
    next?.addEventListener('click', () => showSlide(requestedIndex + 1));
    const stage = gallery.querySelector('.gallery-stage');
    stage?.addEventListener('touchstart', event => {
      touchStart = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : undefined;
    }, { passive: true });
    stage?.addEventListener('touchend', event => {
      if (!touchStart || event.touches.length || variantLoading) { touchStart = undefined; return; }
      const point = event.changedTouches[0];
      const dx = point.clientX - touchStart.x;
      const dy = point.clientY - touchStart.y;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.25) showSlide(requestedIndex + (dx < 0 ? 1 : -1));
      touchStart = undefined;
    }, { passive: true });
    stage?.addEventListener('touchcancel', () => { touchStart = undefined; }, { passive: true });
    quantity?.addEventListener('input', updateTotal);
    quantity?.addEventListener('change', updateTotal);
    updateTotal();
    renderGallery(currentColour, activeIndex);
  };
  const initializeIn = scope => {
    if (scope.matches?.('[data-crispi-variants]')) initialize(scope);
    scope.querySelectorAll('[data-crispi-variants]').forEach(initialize);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeIn(document), { once: true });
  else initializeIn(document);
  document.addEventListener('shopify:section:load', event => initializeIn(event.target));
})();
